import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconBook,
  IconCalendar,
  IconClock,
  IconDownload,
  IconPlayerPlay,
  IconUserCircle,
  IconVideo,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Colors } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { getEllipsisFromString } from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  CourseProps,
  LessonProps,
  PaginationProps,
  RecordingProps,
  TeacherProfileProps,
} from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";
import MegaLoader from "../../Components/MegaLoader";

const CourseRecordings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { courseID } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  // Fetch course details
  const { data: course, isLoading: isLoadingCourse } = usePerformRequest<
    NonPaginatedResponse<CourseProps>
  >({
    method: Endpoints.GetSingleCourseDetails(courseID as string).method,
    route: Endpoints.GetSingleCourseDetails(courseID as string).route,
  });

  // Fetch teacher details
  const { data: teacher, isLoading: isLoadingTeacher } = usePerformRequest<
    NonPaginatedResponse<TeacherProfileProps>
  >({
    method: Endpoints.GetSingleTeacherProfile(course?.data?.teacherID as string).method,
    route: Endpoints.GetSingleTeacherProfile(course?.data?.teacherID as string).route,
  });

  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: recordings,
    isLoading: isLoadingRecordings,
    reloadData: reloadRecordings,
  } = usePerformRequest<PaginatedResponse<RecordingProps[]>>({
    method: Endpoints.GetAllRecordingsUnderCourse(
      courseID as string,
      pagination.page,
      pagination.limit
    ).method,
    route: Endpoints.GetAllRecordingsUnderCourse(
      courseID as string,
      pagination.page,
      pagination.limit
    ).route,
  });

  // Fetch lessons for this course
  const { data: lessons, isLoading: isLoadingLessons } = usePerformRequest<
    PaginatedResponse<LessonProps[]>
  >({
    method: Endpoints.GetLessonsUnderCourse(courseID as string, 1, 100).method,
    route: Endpoints.GetLessonsUnderCourse(courseID as string, 1, 100).route,
  });

  // Filter lessons based on search and status
  const filteredLessons = useMemo(() => {
    if (!lessons?.data) return [];

    let filtered = lessons.data;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (lesson) =>
          lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lesson.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by status (completed vs upcoming)
    if (selectedStatus !== "all") {
      const now = Date.now();
      if (selectedStatus === "completed") {
        filtered = filtered.filter((lesson) => lesson.initialRunTime < now);
      } else if (selectedStatus === "upcoming") {
        filtered = filtered.filter((lesson) => lesson.initialRunTime > now);
      }
    }

    // Sort by date (most recent first)
    return filtered.sort((a, b) => b.initialRunTime - a.initialRunTime);
  }, [lessons, searchQuery, selectedStatus]);

  const formatDate = (timestamp: number) => {
    return dayjs(timestamp).format("MMM D, YYYY h:mm A");
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const isLessonCompleted = (lesson: LessonProps) => {
    const now = Date.now();
    return lesson.initialRunTime < now;
  };

  if (isLoadingCourse || isLoadingLessons) {
    return (
      <Container size="lg" py="xl">
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div className="loading-spinner" />
          <Text mt="md" c="dimmed">
            Loading recordings...
          </Text>
        </div>
      </Container>
    );
  }

  if (!course?.data) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconBook size={20} />} title="Course Not Found" color="red" variant="light">
          <Text>The course you're looking for doesn't exist.</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <div className="course-recordings-container">
      <Container size="lg" py="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Group justify="space-between" align="center" mb="xl">
            <div>
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate(`/d/course/${courseID}`)}
                mb="md"
              >
                {t("navigation.backToCourse")}
              </Button>
              <Title order={1} className="page-title">
                {t("courses.courseRecordings")}
              </Title>
              <Text c="dimmed" mt={4}>
                {course.data.name}
              </Text>
            </div>
            <Badge size="lg" variant="light" color="blue" leftSection={<IconVideo size={16} />}>
              {filteredLessons.length} {t("courses.lessonRecordings")}
            </Badge>
          </Group>
        </motion.div>

        {/* Course Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Card withBorder p="lg" radius="md" mb="xl">
            <Grid gutter="lg">
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Group gap="md" align="flex-start">
                  <Avatar
                    size="lg"
                    radius="md"
                    src={course.data.thumbnail}
                    color={Colors.PurplePrimary}
                  >
                    <IconBook size={24} />
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <Title order={3} mb="xs">
                      {course.data.name}
                    </Title>
                    <Text c="dimmed" size="sm" mb="md" lineClamp={2}>
                      {getEllipsisFromString(course.data.description, 200)}
                    </Text>
                    {teacher?.data && (
                      <Group gap="sm">
                        <IconUserCircle size={16} />
                        <Text size="sm" c="dimmed">
                          Instructor: {teacher.data.name}
                        </Text>
                      </Group>
                    )}
                  </div>
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb="xs">
                      {t("courses.courseStatus")}
                    </Text>
                    <Badge color={course.data.isActive ? "green" : "red"} variant="light" size="lg">
                      {course.data.isActive ? t("common.active") : t("common.inactive")}
                    </Badge>
                  </div>
                  <div>
                    <Text size="sm" fw={500} mb="xs">
                      {t("courses.totalLessons")}
                    </Text>
                    <Text size="lg" fw={600} c={Colors.PurplePrimary}>
                      {lessons?.data?.length || 0}
                    </Text>
                  </div>
                </Stack>
              </Grid.Col>
            </Grid>
          </Card>
        </motion.div>

        {/* Recordings List */}
        {isLoadingRecordings ? (
          <>
            <MegaLoader />
          </>
        ) : (
          <>
            {recordings && recordings.data && recordings.data.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Stack gap="md">
                  {recordings?.data?.map((recording, index) => (
                    <motion.div
                      key={recording.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                    >
                      <Card withBorder p="lg" radius="md" className="recording-card">
                        <Grid gutter="lg">
                          {/* Lesson Info */}
                          <Grid.Col span={{ base: 12, md: 8 }}>
                            <Group gap="md" align="flex-start">
                              <div className="lesson-number">
                                <div className="number-circle">
                                  <Text c="white" fw={500} size="sm">
                                    {index + 1}
                                  </Text>
                                </div>
                              </div>
                              <div style={{ flex: 1 }}>
                                <Title order={4} mb="xs">
                                  {
                                    lessons?.data?.find(
                                      (lesson) => lesson.id === recording.lessonID
                                    )?.title
                                  }
                                </Title>
                                <Text c="dimmed" size="sm" mb="md" lineClamp={2}>
                                  {
                                    lessons?.data?.find(
                                      (lesson) => lesson.id === recording.lessonID
                                    )?.description
                                  }
                                </Text>

                                <div className="flex-row align-center">
                                  <IconCalendar />
                                  <Text ml={5} fz="sm">
                                    {dayjs(recording.createdAt).format("ddd, DD MMM YYYY")} at{" "}
                                    {dayjs(recording.createdAt).format("hh:mm A")}
                                  </Text>
                                </div>
                                <br />
                                <Link to={`/d/recording/${recording.id}`}>
                                  <Button
                                    variant="outline"
                                    color={Colors.PurplePrimary}
                                    onClick={() => navigate(`/d/recording/${recording.id}`)}
                                  >
                                    {t("common.view")}
                                  </Button>
                                </Link>
                              </div>
                            </Group>
                          </Grid.Col>
                        </Grid>
                      </Card>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Alert
                  icon={<IconVideo size={20} />}
                  title={t("courses.noRecordings")}
                  color="blue"
                  variant="light"
                  className="no-recordings-alert"
                >
                  <Text>
                    No recordings found for this course. Check back later for new content!
                  </Text>
                </Alert>
              </motion.div>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default CourseRecordings;
